import { memo, useCallback, useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { generatePath, useHistory } from 'react-router-dom'

import { useAppDispatch, useAppSelector } from 'app/hooks'
import ROUTES from 'app/routes'
import RadioTiles from 'components/RadioTiles/RadioTiles'
import { CommonSearch } from 'components/Table'
import { KebabMenu, KebabMenuItem } from 'components/KebabMenu'

import {
  JobsMainFiltersWrapper,
  JobsMainRadioTilesWrapper,
  JobsSearchDesktopWrapper,
  JobsSearchMobileWrapper,
  SelectedDomainTitle,
  StyledDomainActions,
  StyledDomainHeader,
  StyledDomainInfo,
  StyledJobsMain,
  TableWrapper,
} from './styles'
import {
  currentRootDomainIdSelector,
  rootDomainByIdSelector,
} from 'features/rootDomains/selectors'
import DomainJobsHomes from 'features/domains/components/DomainJobsHomes'
import DomainPendingJobs from 'features/domains/components/DomainPendingJobs'
import { DomainsSelector } from 'features/DomainsSelector'
import { jobsMainMessages } from './messages'
import type { RadioTileOptionType } from 'components/RadioTiles/RadioTiles'
import {
  domainJobsWithHomesActions,
  domainPendingJobsActions,
  selectedNavDomainActions,
} from 'features/domains/slice'
import { AddButton, AddButtonItem } from 'features/domains/components/AddButton'
import ClaimHomeModal from '../ClaimHomeModal'
import { domainToDomainDataCacheSelector } from 'features/domains/selectors'
import NewDomainModal from '../NewDomainModal'
import RenameDomainModal from 'features/domains/components/RenameDomainModal'
import ExportDomainModal from 'features/domains/components/ExportDomainModal'
import DeleteDomainModal from 'features/domains/components/DeleteDomainModal'
import { DOMAIN_ADMIN } from 'features/auth/permissions/consts'
import Permissions from 'features/auth/permissions'
import { domainNestingEnabledSelector } from 'features/auth/permissions/selectors'
import useCurrentSelectedDomainId from 'features/DomainsSelector/hooks'

enum JOBS_LISTS {
  homes = 'homesJobs',
  pending = 'pendingJobs',
}

function JobsMain() {
  const dispatch = useAppDispatch()
  const history = useHistory()
  const { formatMessage } = useIntl()
  const [searchValue, setSearchValue] = useState<string>('')
  const [selectedJobsList, setSelectedJobsList] = useState<JOBS_LISTS>(
    JOBS_LISTS.homes
  )
  const [isAddHomeOpen, setIsAddHomeOpen] = useState(false)
  const [isNewDomainOpen, setIsNewDomainOpen] = useState(false)
  const [isRenameDomainOpen, setIsRenameDomainOpen] = useState(false)
  const [isDeleteDomainOpen, setIsDeleteDomainOpen] = useState(false)
  const [isExportDomainOpen, setIsExportDomainOpen] = useState(false)

  const currentRootId = useAppSelector(currentRootDomainIdSelector)
  const currentRootDomain = useAppSelector(
    rootDomainByIdSelector(currentRootId)
  )
  const hasDomainNesting = useAppSelector(domainNestingEnabledSelector)

  const domainsCache = useAppSelector(domainToDomainDataCacheSelector)

  const domainId = useCurrentSelectedDomainId()
  const domain = domainsCache[domainId]

  const domainDepth = domain?.depth
  const domainName = domain?.name

  const isMaxDomainDepth =
    domainDepth &&
    currentRootDomain &&
    domainDepth >= currentRootDomain.maxAllowedDepth

  const handleJobsListsToggle = useCallback((val: JOBS_LISTS) => {
    setSelectedJobsList(val)
  }, [])

  const jobsListsOptions: RadioTileOptionType<JOBS_LISTS>[] = [
    {
      label: formatMessage(jobsMainMessages.homes),
      value: JOBS_LISTS.homes,
      defaultChecked: selectedJobsList === JOBS_LISTS.homes,
    },
    {
      label: formatMessage(jobsMainMessages.pending),
      value: JOBS_LISTS.pending,
      defaultChecked: selectedJobsList === JOBS_LISTS.pending,
    },
  ]

  useEffect(() => {
    // clear fetched jobs and selected sub-domain when jobs page unmounts
    return () => {
      dispatch(domainJobsWithHomesActions.clear())
      dispatch(domainPendingJobsActions.clear())
      dispatch(selectedNavDomainActions.clear())
    }
  }, [dispatch])

  useEffect(() => {
    setSearchValue('')
  }, [selectedJobsList])

  const onClaimHomeSuccess = useCallback(
    ({ homeId }: { homeId: string }) => {
      if (currentRootId) {
        history.push(
          generatePath(ROUTES.homeStatus, {
            homeId,
            rootDomainId: currentRootId,
          })
        )
      }
    },
    [history, currentRootId]
  )

  const canAddDomain =
    (hasDomainNesting || domainId === currentRootId) && !isMaxDomainDepth
  const shouldRenderNewDomainModal = !isMaxDomainDepth && isNewDomainOpen
  const canRenameDomain = !!domain && !domain.root
  const shouldRenderRenameDomainModal = canRenameDomain && isRenameDomainOpen
  const shouldRenderExportDomainModal = isExportDomainOpen
  const canDeleteDomain = !!domain && !domain.root
  const shouldRenderDeleteDomainModal = canDeleteDomain && isDeleteDomainOpen
  const shouldRenderKebab = canRenameDomain || canDeleteDomain

  if (!currentRootId) {
    return null
  }

  return (
    <>
      {isAddHomeOpen && domainId && (
        <ClaimHomeModal
          domainId={domainId}
          onClose={() => setIsAddHomeOpen(false)}
          onAddSuccess={onClaimHomeSuccess}
        />
      )}

      {shouldRenderNewDomainModal && !!domainId && !!domainName && (
        <NewDomainModal
          domainId={domainId}
          domainName={domainName}
          onClose={() => setIsNewDomainOpen(false)}
        />
      )}

      {shouldRenderRenameDomainModal && !!domain && (
        <RenameDomainModal
          domain={domain}
          onClose={() => setIsRenameDomainOpen(false)}
        />
      )}

      {shouldRenderDeleteDomainModal && !!domain && (
        <DeleteDomainModal
          domain={domain}
          onClose={() => setIsDeleteDomainOpen(false)}
        />
      )}

      {shouldRenderExportDomainModal && !!domain && (
        <ExportDomainModal
          domain={domain}
          onClose={() => setIsExportDomainOpen(false)}
        />
      )}

      <StyledJobsMain>
        <JobsMainFiltersWrapper>
          <DomainsSelector />
          <JobsSearchDesktopWrapper>
            <CommonSearch
              setSearchValue={setSearchValue}
              placeholder={
                selectedJobsList === JOBS_LISTS.homes
                  ? formatMessage(jobsMainMessages.searchHomes)
                  : formatMessage(jobsMainMessages.searchJobs)
              }
              disableQuerySearch
              externalSearchValue={searchValue}
            />
          </JobsSearchDesktopWrapper>
        </JobsMainFiltersWrapper>

        <JobsSearchMobileWrapper>
          <CommonSearch
            setSearchValue={setSearchValue}
            placeholder={
              selectedJobsList === JOBS_LISTS.homes
                ? formatMessage(jobsMainMessages.searchHomes)
                : formatMessage(jobsMainMessages.searchJobs)
            }
            disableQuerySearch
            externalSearchValue={searchValue}
          />
        </JobsSearchMobileWrapper>

        <StyledDomainHeader>
          <StyledDomainInfo>
            <SelectedDomainTitle>{domain?.name}</SelectedDomainTitle>
          </StyledDomainInfo>

          <JobsMainRadioTilesWrapper>
            <RadioTiles
              options={jobsListsOptions}
              name="jobs_lists"
              onChange={handleJobsListsToggle}
            />
          </JobsMainRadioTilesWrapper>

          <StyledDomainActions>
            <AddButton
              title={<FormattedMessage {...jobsMainMessages.addBtn} />}
            >
              <Permissions allow={[DOMAIN_ADMIN]} domainId={domainId}>
                {canAddDomain && (
                  <AddButtonItem
                    title={<FormattedMessage {...jobsMainMessages.newGroup} />}
                    onSelect={() => setIsNewDomainOpen(true)}
                  />
                )}
              </Permissions>
              <AddButtonItem
                title={<FormattedMessage {...jobsMainMessages.newHome} />}
                onSelect={() => setIsAddHomeOpen(true)}
              />
            </AddButton>
            <Permissions allow={[DOMAIN_ADMIN]} domainId={domainId}>
              {shouldRenderKebab && (
                <KebabMenu>
                  {canRenameDomain && (
                    <KebabMenuItem
                      title={
                        <FormattedMessage {...jobsMainMessages.renameGroup} />
                      }
                      onClick={() => setIsRenameDomainOpen(true)}
                    />
                  )}
                  {canDeleteDomain && (
                    <KebabMenuItem
                      title={
                        <FormattedMessage {...jobsMainMessages.deleteGroup} />
                      }
                      onClick={() => setIsDeleteDomainOpen(true)}
                    />
                  )}
                  <KebabMenuItem
                      title={
                        <FormattedMessage {...jobsMainMessages.exportGroup} />
                      }
                      onClick={() => setIsExportDomainOpen(true)}
                    />
                </KebabMenu>
              )}
              {!shouldRenderKebab && (
                <KebabMenu>
                  <KebabMenuItem
                      title={
                        <FormattedMessage {...jobsMainMessages.exportDomain} />
                      }
                      onClick={() => setIsExportDomainOpen(true)}
                    />
                </KebabMenu>
              )}
            </Permissions>
          </StyledDomainActions>
        </StyledDomainHeader>

        {/* TODO: during rewrite reduce overhead by only loading shown tables, and then caching data */}
        <TableWrapper hidden={selectedJobsList === JOBS_LISTS.pending}>
          <DomainJobsHomes searchValue={searchValue} />
        </TableWrapper>

        <TableWrapper hidden={selectedJobsList === JOBS_LISTS.homes}>
          <DomainPendingJobs searchValue={searchValue} />
        </TableWrapper>
      </StyledJobsMain>
    </>
  )
}

export default memo(JobsMain)
